import { useEffect, useState } from 'react';

import type { CallStartParams, CallState } from '@entities/call-item';

import { hasHostBridgeContext, hostBridgeAdapter } from '../lib/host-bridge-adapter';
import type { HistoricalCall } from '../model/types';

type TrackableBridgeCall = Pick<HistoricalCall, 'id' | 'state' | 'direction'>;
type BridgeTracking = {
	sessionId: string;
	seenInStore: boolean;
	calledSent: boolean;
	callId: string | null;
};

interface UseHostBridgeParams {
	isAuthorized: boolean;
	isRegistered: boolean;
	allCalls: TrackableBridgeCall[];
	startOutgoingCall: (params: CallStartParams) => Promise<string | null>;
	getCallIdBySessionId: (sessionId: string) => string | null;
	hangup: (id?: string) => void;
}

const isBridgeCallFinished = (state: CallState) => state === 'accepted' || state === 'declined';
const isCallInProgress = (state: CallState) => !isBridgeCallFinished(state);

/**
 * Связывает call-feature с host bridge API.
 *
 * Обязанности:
 * - отправляет `logged-in`, когда клиент готов к звонку;
 * - обрабатывает входящие команды `call`/`drop` от host;
 * - трекает bridge-звонок и отправляет `called`/`closed` с `callId`.
 */
export const useHostBridge = ({
	isAuthorized,
	isRegistered,
	allCalls,
	startOutgoingCall,
	getCallIdBySessionId,
	hangup,
}: UseHostBridgeParams) => {
	const [bridgeTracking, setBridgeTracking] = useState<BridgeTracking | null>(null);
	const isReadyToCall = isAuthorized && isRegistered;
	const hasInProgressCall = allCalls.some((callItem) => isCallInProgress(callItem.state));

	/** Сообщаем host, что клиент готов принимать команду `call`. */
	useEffect(() => {
		if (!isReadyToCall) return;
		hostBridgeAdapter.send({ action: 'logged-in' });
	}, [isReadyToCall]);

	/** Обрабатывает входящие команды host (`call`/`drop`). */
	useEffect(() => {
		const unsubscribe = hostBridgeAdapter.subscribe((event) => {
			switch (event.action) {
				case 'drop': {
					if (bridgeTracking?.sessionId) {
						hangup(bridgeTracking.sessionId);
					}
					return;
				}
				case 'call': {
					if (!isReadyToCall || bridgeTracking || hasInProgressCall) return;

					startOutgoingCall({
						aor: event.target,
						displayName: event.target,
						withVideo: false,
					})
						.then((sessionId) => {
							if (!sessionId) return;

							setBridgeTracking((currentTracking) => {
								if (currentTracking) return currentTracking;

								return {
									sessionId,
									seenInStore: false,
									calledSent: false,
									callId: getCallIdBySessionId(sessionId),
								};
							});
						})
						.catch(() => {});
					break;
				}
				default: {
					break;
				}
			}
		});

		return unsubscribe;
	}, [
		bridgeTracking,
		hangup,
		hasInProgressCall,
		isReadyToCall,
		startOutgoingCall,
		getCallIdBySessionId,
	]);

	/**
	 * Трекает bridge-звонок и отправляет lifecycle-события `called`/`closed`.
	 *
	 * Сценарии:
	 * - автотрекинг исходящего звонка, если клиент открыт с `target`
	 *   и bridge-команда `call` не проходила через адаптер;
	 * - сопровождение уже tracked-сессии до завершения.
	 */
	useEffect(() => {
		if (!bridgeTracking) {
			if (!hasHostBridgeContext()) return;

			const currentOutgoingCall = allCalls.find(
				(callItem) =>
					callItem.direction === 'outgoing' &&
					(callItem.state === 'calling' || callItem.state === 'active')
			);
			if (!currentOutgoingCall) return;

			const resolvedCallId = getCallIdBySessionId(currentOutgoingCall.id);
			const isActiveCall = currentOutgoingCall.state === 'active';
			if (isActiveCall) {
				hostBridgeAdapter.send({ action: 'called', param: { callId: resolvedCallId } });
			}

			setBridgeTracking({
				sessionId: currentOutgoingCall.id,
				seenInStore: true,
				calledSent: isActiveCall,
				callId: resolvedCallId,
			});
			return;
		}

		const {
			sessionId,
			seenInStore,
			calledSent: trackedCalledSent,
			callId: cachedCallId,
		} = bridgeTracking;
		const trackedCallState = allCalls.find((callItem) => callItem.id === sessionId);
		const resolvedCallId = getCallIdBySessionId(sessionId) ?? cachedCallId;

		if (!trackedCallState) {
			if (!seenInStore) return;

			hostBridgeAdapter.send({ action: 'closed', param: { callId: resolvedCallId } });
			setBridgeTracking(null);
			return;
		}

		let calledSent = trackedCalledSent;
		if (trackedCallState.state === 'active' && !calledSent) {
			hostBridgeAdapter.send({ action: 'called', param: { callId: resolvedCallId } });
			calledSent = true;
		}

		if (isBridgeCallFinished(trackedCallState.state)) {
			hostBridgeAdapter.send({ action: 'closed', param: { callId: resolvedCallId } });
			setBridgeTracking(null);
			return;
		}

		const nextSeenInStore = true;
		if (
			cachedCallId === resolvedCallId &&
			trackedCalledSent === calledSent &&
			seenInStore === nextSeenInStore
		) {
			return;
		}

		setBridgeTracking((currentTracking) => {
			if (!currentTracking || currentTracking.sessionId !== sessionId) {
				return currentTracking;
			}

			return {
				...currentTracking,
				callId: resolvedCallId,
				calledSent,
				seenInStore: true,
			};
		});
	}, [allCalls, bridgeTracking, getCallIdBySessionId]);
};
